// Fixed-step sim orchestration. 1 tick = 1 in-game minute.
// The UI never mutates state; it dispatches commands that are validated here.

import { TUNABLES } from "@degen/shared/config/tunables";
import { Rng, streamRng } from "./rng";
import { createGameState, dayOf, minuteOfDay, pushLog, type GameState } from "./state";
import { addHeat, addTrust, netWorth, type NewPlayerInput } from "./player";
import { rankIndex, rankName } from "./rank";
import { liveTokens, marketBuy, marketNewDay, marketSell, marketTick } from "./market/market";
import { scheduleSpawns, spawnNpcToken } from "./market/launcher";
import { balanceOf } from "./market/token";
import { followAccount, onPlayerSell, playerPost, socialNewDay, socialTick } from "./social/feed";
import type { PlayerPostType } from "@degen/shared/config/content";
import { airdropsNewDay, doFarmTask, seedProtocols, type FarmTask } from "./airdrops/protocols";
import { cancelListing, engageForWl, launchCollection, listNft, mintNft, nftNewDay, nftTick, sellNftNow, type LaunchCollectionInput } from "./nft/drops";
import { launchAirdrop, launchCoin, launchesNewDay, onPlayerSellOwnToken, type AirdropInput, type LaunchCoinInput } from "./market/playerLaunch";
import { eventsTick } from "./events/sleuth";
import { enterPresale } from "./market/presale";
import { buyCar, sellCar, setActiveCar } from "./cars/cars";
import { giveRide } from "./cars/rides";
import { APARTMENT_RANK, buyHouse, cook, upgradeStarterHome, housesNewDay, moveFurniture, moveIn, placeFurniture, removeFurniture, sellHouse, type Placement } from "./houses/houses";
import { markRead, messagesTick, sendMessage, welcomeMessage } from "./messages/messages";
import { giveawaysTick, hostGiveaway, joinGiveaway } from "./social/giveaways";
import { resolveEvent, rollHourlyEvents } from "./events/random";
import { checkRekt, comeback, imCooked } from "./rekt";
import { tutorialOnCommand, type TutorialAction } from "./tutorial";
import { dailyCheckIn, gasFee, outOfGasMessage, swapDgn } from "./gas";
import { cancel, setSnipe, subscribe, vanoNewDay, vanoTick, type VanoTier } from "./vano/vano";
import { hackathonsNewDay, hackBuild, joinHackathon } from "./hackathons/hackathons";
import { buyPhone, setPhone } from "./phones/phones";
import { closePerp, openPerp, perpsTick } from "./perps/perps";
import { checkAchievements, equip, progressOnCommand, questsNewDay, type CosmeticSlot } from "./progress/progress";
import { moderateText } from "@degen/shared/moderation";
import { money } from "@degen/shared/money";
import { adoptPet, feedPet, hasPerk, perkAmount, petPet, petsNewDay, setCompanion, type PetKind } from "./pets/pets";

export type Command =
  | { type: "BUY_TOKEN"; tokenId: string; usd: number }
  | { type: "SELL_TOKEN"; tokenId: string; tokens?: number; pct?: number }
  | { type: "COFFEE" }
  | { type: "TOUCH_GRASS" }
  | { type: "LAY_LOW" }
  | { type: "SLEEP" }
  | { type: "POST"; postType: PlayerPostType; variant: number; tokenId?: string; targetId?: string }
  | { type: "FOLLOW"; accountId: string }
  | { type: "FARM_TASK"; protocolId: string; task: FarmTask }
  | { type: "WL_ENGAGE"; collectionId: string }
  | { type: "MINT_NFT"; collectionId: string; qty: number }
  | { type: "LIST_NFT"; collectionId: string; qty: number; price: number }
  | { type: "CANCEL_LISTING"; collectionId: string }
  | { type: "SELL_NFT_NOW"; collectionId: string; qty: number }
  | ({ type: "LAUNCH_COIN" } & LaunchCoinInput)
  | ({ type: "LAUNCH_COLLECTION" } & LaunchCollectionInput)
  | ({ type: "LAUNCH_AIRDROP" } & AirdropInput)
  | { type: "RESOLVE_EVENT"; eventId: string; choice: string }
  | { type: "COMEBACK" }
  | { type: "IM_COOKED" }
  | { type: "TUTORIAL"; action: TutorialAction }
  | { type: "ADOPT_PET"; kind: PetKind; name: string }
  | { type: "FEED_PET"; petId: string }
  | { type: "PET_PET"; petId: string }
  | { type: "SET_COMPANION"; petId: string | null }
  | { type: "ENTER_PRESALE"; presaleId: string }
  | { type: "BUY_CAR"; modelId: string; color: string }
  | { type: "GIVE_RIDE"; accountId: string; meters: number; modelId: string }
  | { type: "SELL_CAR"; carId: string }
  | { type: "SET_ACTIVE_CAR"; carId: string }
  | { type: "BUY_HOUSE"; houseId: string }
  | { type: "SELL_HOUSE"; houseId: string }
  | { type: "MOVE_IN"; houseId: string }
  | { type: "PLACE_FURNITURE"; houseId: string; placement: Placement }
  | { type: "MOVE_FURNITURE"; houseId: string; index: number; placement: Placement }
  | { type: "REMOVE_FURNITURE"; houseId: string; index: number }
  | { type: "COOK" }
  | { type: "SEND_MESSAGE"; contactId: string; text: string }
  | { type: "READ_THREAD"; contactId: string }
  | { type: "JOIN_GIVEAWAY"; giveawayId: string }
  | { type: "HOST_GIVEAWAY"; prizeUsd: number; hours: number }
  | { type: "DAILY_CHECKIN" }
  | { type: "SWAP_DGN"; side: "buy" | "sell"; amount: number }
  | { type: "VANO_SUBSCRIBE"; tier: VanoTier }
  | { type: "VANO_CANCEL" }
  | { type: "VANO_SNIPE"; wusd: number }
  | { type: "JOIN_HACKATHON"; hackathonId: string; track: string }
  | { type: "HACK_BUILD"; hackathonId: string }
  | { type: "BUY_PHONE"; model: string }
  | { type: "SET_PHONE"; model: string }
  | { type: "WELCOME" }
  | { type: "OPEN_PERP"; tokenId: string; side: "long" | "short"; marginDgn: number; leverage: number }
  | { type: "CLOSE_PERP"; id: number }
  | { type: "POST_FREE"; text: string; tokenId?: string }
  | { type: "EQUIP"; slot: CosmeticSlot; item: string | null; server?: string[] }
  // Shared worlds: the server filled a trade / launch; apply it to your personal state.
  | { type: "APPLY_FILL"; fillId: number; tokenId: string; side: "buy" | "sell"; usd: number; tokens: number; fee: number; remaining: number }
  | { type: "APPLY_LAUNCH"; tokenId: string; feeUsd: number; devUsd: number; devTokens: number };

export type CommandResult = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export type EngineEvent =
  | { type: "newDay"; day: number }
  | { type: "rankChange"; from: number; to: number }
  | { type: "rekt" };

const P = TUNABLES.player;
const TPD = TUNABLES.clock.ticksPerDay;

export class Engine {
  private rngMarket: Rng;
  private rngNpc: Rng;
  private rngSocial: Rng;
  private rngAirdrops: Rng;
  private rngNft: Rng;
  private rngEvents: Rng;
  private rngLife: Rng;
  private rngVano: Rng;
  private rngHack: Rng;
  private rngProgress: Rng;
  private listeners: ((e: EngineEvent) => void)[] = [];

  constructor(public readonly state: GameState) {
    this.rngMarket = streamRng(state.rng, state.seed, "market");
    this.rngNpc = streamRng(state.rng, state.seed, "npc");
    this.rngSocial = streamRng(state.rng, state.seed, "social");
    this.rngAirdrops = streamRng(state.rng, state.seed, "airdrops");
    this.rngNft = streamRng(state.rng, state.seed, "nft");
    this.rngEvents = streamRng(state.rng, state.seed, "events");
    this.rngLife = streamRng(state.rng, state.seed, "life");
    this.rngVano = streamRng(state.rng, state.seed, "vano");
    this.rngHack = streamRng(state.rng, state.seed, "hackathons");
    this.rngProgress = streamRng(state.rng, state.seed, "progress");
  }

  /** Create a fresh world, seed it with NPC tokens, and pre-simulate to wake time. */
  static newGame(seed: number, input: NewPlayerInput): Engine {
    const engine = new Engine(createGameState(seed, input));
    engine.state.run = { input: structuredClone(input), actions: [] };
    questsNewDay(engine.state, engine.rngProgress, 1); // day 1 quests (day 1 has no morning boundary)
    const s = engine.state;
    scheduleSpawns(s, engine.rngMarket);
    for (let i = 0; i < TUNABLES.market.initialTokens; i++) spawnNpcToken(s, engine.rngMarket, 0);
    seedProtocols(s, engine.rngAirdrops);
    nftNewDay(s, engine.rngNft, 0); // drops minting on day 1
    engine.advance(TUNABLES.clock.wakeMinute);
    engine.updateDerived(true);
    return engine;
  }

  on(fn: (e: EngineEvent) => void): () => void {
    this.listeners.push(fn);
    return () => (this.listeners = this.listeners.filter((l) => l !== fn));
  }

  private emit(e: EngineEvent) {
    for (const l of this.listeners) l(e);
  }

  /** Advance the sim by `n` ticks. */
  advance(n: number) {
    for (let i = 0; i < n; i++) this.tick();
    this.updateDerived();
  }

  private tick() {
    const s = this.state;
    s.tick++;
    if (minuteOfDay(s.tick) === 0) this.onNewDay();
    if (!s.shared) marketTick(s, this.rngMarket, this.rngNpc); // shared worlds: the server runs the market
    nftTick(s, this.rngNft);
    eventsTick(s, this.rngEvents);
    if (s.tick % 60 === 0) rollHourlyEvents(s, this.rngEvents);
    socialTick(s, this.rngSocial);
    messagesTick(s, this.rngLife);
    perpsTick(s);
    // Vano runs after the market so it sees this tick's launches; snipes go through the normal buy path (gas, holdings).
    for (const sn of vanoTick(s, this.rngVano)) {
      this.depth++; // derived action: not part of the recorded command log
      try {
        const r = this.dispatch({ type: "BUY_TOKEN", tokenId: sn.tokenId, usd: Math.min(sn.wusd, s.player.cash) });
        if (r.ok) pushLog(s, "vano_snipe", `🤖 Vano sniped ${money(r.data?.usd ?? 0)} of $${s.market.tokens[sn.tokenId]?.ticker} at launch.`, { tokenId: sn.tokenId });
      } finally {
        this.depth--;
      }
    }
    giveawaysTick(s, this.rngLife);
  }

  private onNewDay() {
    const s = this.state;
    const p = s.player;
    const day = dayOf(s.tick);
    const decay = P.heatDecayPerDay * (p.daily.layLow ? P.layLowHeatDecayMultiplier : 1) + (hasPerk(s, "cat") ? perkAmount("cat") : 0);
    addHeat(p, -decay);
    socialNewDay(s); // follower decay looks at yesterday's posts, before counters reset
    airdropsNewDay(s, this.rngAirdrops, day);
    nftNewDay(s, this.rngNft, day);
    launchesNewDay(s);
    vanoNewDay(s, day);
    hackathonsNewDay(s, this.rngHack, day);
    questsNewDay(s, this.rngProgress, day);
    checkAchievements(s); // passive ones (net worth, rank, streaks)
    petsNewDay(s);
    if (hasPerk(s, "dog")) p.followers += perkAmount("dog");
    housesNewDay(s); // living at home cools heat a bit more
    p.day = day;
    p.stats.daysPlayed++;
    p.daily = { coffees: 0, layLow: false, posts: 0, postTypes: {} };
    if (!s.shared) marketNewDay(s, this.rngMarket, this.rngNpc, day);
    this.emit({ type: "newDay", day });
  }

  /** Recompute cached rank and peak stats; emit rank changes. */
  updateDerived(silent = false) {
    const s = this.state;
    const p = s.player;
    const nw = netWorth(s);
    if (nw > p.stats.peakNetWorth) p.stats.peakNetWorth = nw;
    if (checkRekt(s) && !silent) this.emit({ type: "rekt" });
    const r = rankIndex(nw, p.followers);
    if (r !== p.rank) {
      const from = p.rank;
      p.rank = r;
      if (!silent) {
        pushLog(s, r > from ? "rank_up" : "rank_down", `${r > from ? "Ranked up to" : "Downgraded to"} ${rankName(r)}.`);
        this.emit({ type: "rankChange", from, to: r });
      }
      if (r >= APARTMENT_RANK) upgradeStarterHome(s);
    }
  }

  private depth = 0;

  /** Player commands. Outer calls are recorded for replay (internal ones, like Vano snipes, are derived). */
  dispatch(cmd: Command): CommandResult {
    if (this.depth === 0 && this.state.run) this.state.run.actions.push([this.state.tick, cmd]);
    this.depth++;
    try {
      return this.dispatchInner(cmd);
    } finally {
      this.depth--;
    }
  }

  private dispatchInner(cmd: Command): CommandResult {
    if (this.state.rekt && cmd.type !== "COMEBACK" && cmd.type !== "TUTORIAL") return fail("You're rekt. Comeback or start a new life.");
    // Gas: on-chain actions burn a little DGN, charged only when the action goes through.
    const fee = gasFee(cmd);
    const p = this.state.player;
    if (fee > 0 && p.dgn + 1e-9 < fee) return fail(outOfGasMessage(fee, p.dgn));
    const res = this.apply(cmd);
    if (res.ok && fee > 0) {
      p.dgn = Math.max(0, p.dgn - fee);
      p.gas.spent += fee;
      res.data = { ...res.data, gas: fee };
    }
    if (res.ok) {
      progressOnCommand(this.state, cmd);
      tutorialOnCommand(this.state, this.rngEvents, cmd);
      this.updateDerived();
    }
    return res;
  }

  private apply(cmd: Command): CommandResult {
    const s = this.state;
    const p = s.player;
    switch (cmd.type) {
      case "COFFEE": {
        const c = P.coffee;
        if (p.daily.coffees >= c.maxPerDay) return fail("Two coffees is the limit. Your heart says thanks.");
        if (p.cash < c.costUsd) return fail("Not enough cash for coffee.");
        p.cash -= c.costUsd;
        p.daily.coffees++;
        addHeat(p, c.heat);
        return { ok: true };
      }
      case "TOUCH_GRASS": {
        const g = P.touchGrass;
        if ((p.daily.grass ?? 0) >= g.maxPerDay) return fail("You've touched enough grass today. The grass needs a break too.");
        p.daily.grass = (p.daily.grass ?? 0) + 1;
        addHeat(p, g.heat);
        addTrust(p, g.trust);
        return { ok: true };
      }
      case "LAY_LOW": {
        if (p.daily.posts > 0) return fail("You already posted today. Laying low starts tomorrow.");
        p.daily.layLow = true;
        return { ok: true };
      }
      case "SLEEP": {
        // Shared worlds run on the server clock: you rest, but time doesn't skip.
        if (s.shared) {
          addHeat(p, -2);
          return { ok: true, data: { ticks: 0 } };
        }
        const m = minuteOfDay(s.tick);
        const wake = TUNABLES.clock.wakeMinute;
        const ticks = m < wake ? wake - m : TPD - m + wake;
        s.events.asleep = true; // no scam DMs in your dreams
        this.advance(ticks);
        s.events.asleep = false;
        return { ok: true, data: { ticks } };
      }
      case "POST": {
        const r = playerPost(s, this.rngSocial, cmd);
        return r.ok ? { ok: true, data: { followers: r.followers, engagement: r.engagement } } : fail(r.error);
      }
      case "FOLLOW":
        return followAccount(s, cmd.accountId) ? { ok: true } : fail("No such account.");
      case "RESOLVE_EVENT":
        return resolveEvent(s, this.rngEvents, cmd.eventId, cmd.choice);
      case "COMEBACK":
        return comeback(s) ? { ok: true } : fail("You're not rekt.");
      case "IM_COOKED":
        imCooked(s);
        return { ok: true };
      case "TUTORIAL":
        return { ok: true };
      case "BUY_CAR":
        return buyCar(s, this.rngLife, cmd.modelId, cmd.color);
      case "GIVE_RIDE":
        return giveRide(s, this.rngLife, cmd.accountId, cmd.meters, cmd.modelId);
      case "SELL_CAR":
        return sellCar(s, cmd.carId);
      case "SET_ACTIVE_CAR":
        return setActiveCar(s, cmd.carId);
      case "BUY_HOUSE":
        return buyHouse(s, cmd.houseId);
      case "SELL_HOUSE":
        return sellHouse(s, cmd.houseId);
      case "MOVE_IN":
        return moveIn(s, cmd.houseId);
      case "PLACE_FURNITURE":
        return placeFurniture(s, cmd.houseId, cmd.placement);
      case "MOVE_FURNITURE":
        return moveFurniture(s, cmd.houseId, cmd.index, cmd.placement);
      case "REMOVE_FURNITURE":
        return removeFurniture(s, cmd.houseId, cmd.index);
      case "COOK":
        return cook(s);
      case "SEND_MESSAGE":
        return sendMessage(s, this.rngLife, cmd.contactId, cmd.text);
      case "READ_THREAD":
        markRead(s, cmd.contactId);
        return { ok: true };
      case "JOIN_GIVEAWAY":
        return joinGiveaway(s, cmd.giveawayId);
      case "HOST_GIVEAWAY":
        return hostGiveaway(s, this.rngLife, cmd.prizeUsd, cmd.hours);
      case "ENTER_PRESALE":
        return enterPresale(s, cmd.presaleId);
      case "ADOPT_PET":
        return adoptPet(s, this.rngEvents, cmd.kind, cmd.name);
      case "FEED_PET":
        return feedPet(s, cmd.petId);
      case "PET_PET":
        return petPet(s, cmd.petId);
      case "SET_COMPANION":
        return setCompanion(s, cmd.petId);
      case "FARM_TASK": {
        const r = doFarmTask(s, cmd.protocolId, cmd.task);
        return r.ok ? { ok: true, data: { points: r.points } } : fail(r.error);
      }
      case "WL_ENGAGE":
        return engageForWl(s, cmd.collectionId);
      case "MINT_NFT":
        return mintNft(s, this.rngNft, cmd.collectionId, cmd.qty);
      case "LIST_NFT":
        return listNft(s, cmd.collectionId, cmd.qty, cmd.price);
      case "CANCEL_LISTING":
        return cancelListing(s, cmd.collectionId);
      case "SELL_NFT_NOW":
        return sellNftNow(s, cmd.collectionId, cmd.qty);
      case "LAUNCH_COIN": {
        const r = launchCoin(s, this.rngMarket, cmd);
        return r.ok ? { ok: true } : fail(r.error);
      }
      case "LAUNCH_COLLECTION":
        return launchCollection(s, this.rngNft, cmd);
      case "LAUNCH_AIRDROP": {
        const r = launchAirdrop(s, this.rngNpc, cmd);
        return r.ok ? { ok: true } : fail(r.error);
      }
      case "VANO_SUBSCRIBE":
        return subscribe(s, cmd.tier);
      case "VANO_CANCEL":
        return cancel(s);
      case "VANO_SNIPE":
        return setSnipe(s, cmd.wusd);
      case "JOIN_HACKATHON":
        return joinHackathon(s, cmd.hackathonId, cmd.track);
      case "HACK_BUILD":
        return hackBuild(s, this.rngHack, cmd.hackathonId);
      case "BUY_PHONE":
        return buyPhone(s, cmd.model);
      case "SET_PHONE":
        return setPhone(s, cmd.model);
      case "APPLY_FILL":
        return this.applyFill(cmd);
      case "APPLY_LAUNCH": {
        if (!s.shared) return fail("Only in shared worlds.");
        p.cash = Math.max(0, p.cash - cmd.feeUsd - cmd.devUsd);
        p.launches.push({ tokenId: cmd.tokenId, launchTick: s.tick, devTokens: cmd.devTokens, soldTokens: 0, rugged: false, lastAirdropDay: 0 });
        p.stats.coinsLaunched++;
        if (cmd.devUsd > 0) p.holdings.push({ kind: "token", tokenId: cmd.tokenId, costUsd: cmd.devUsd });
        return { ok: true };
      }
      case "OPEN_PERP":
        return openPerp(s, cmd.tokenId, cmd.side, cmd.marginDgn, cmd.leverage);
      case "CLOSE_PERP":
        return closePerp(s, cmd.id);
      case "POST_FREE": {
        const m = moderateText(cmd.text, "post", 280);
        if (!m.ok) return fail(m.reason);
        const r = playerPost(s, this.rngSocial, { postType: "meme", variant: 0, tokenId: cmd.tokenId, text: m.text });
        return r.ok ? { ok: true, data: { followers: r.followers, engagement: r.engagement } } : fail(r.error);
      }
      case "EQUIP":
        return equip(s, cmd.slot, cmd.item, cmd.server);
      case "WELCOME":
        welcomeMessage(s);
        return { ok: true };
      case "DAILY_CHECKIN":
        return dailyCheckIn(s);
      case "SWAP_DGN":
        return swapDgn(s, cmd.side, cmd.amount);
      case "BUY_TOKEN":
        return this.buy(cmd.tokenId, cmd.usd);
      case "SELL_TOKEN":
        return this.sell(cmd.tokenId, cmd.tokens, cmd.pct);
    }
  }

  private buy(tokenId: string, usd: number): CommandResult {
    const s = this.state;
    const p = s.player;
    const t = s.market.tokens[tokenId];
    if (!t || t.phase === "dead") return fail("That token is gone.");
    if (!(usd > 0) || !Number.isFinite(usd)) return fail("Enter an amount.");
    if (usd > p.cash + 1e-9) return fail("Not enough cash.");
    const r = marketBuy(s, t, "player", Math.min(usd, p.cash));
    if (!r.ok) return fail("Trade failed.");
    p.cash -= r.usd;
    if (p.cash < 1e-9) p.cash = 0;
    p.stats.trades++;
    if (s.tick - t.bornTick <= P.apeUnknown.maxAgeTicks) addHeat(p, P.apeUnknown.heat);
    const h = p.holdings.find((x) => x.tokenId === tokenId);
    if (h) h.costUsd += r.usd;
    else p.holdings.push({ kind: "token", tokenId, costUsd: r.usd });
    return { ok: true, data: { usd: r.usd, tokens: r.tokens, fee: r.fee } };
  }

  private sell(tokenId: string, tokens?: number, pct?: number): CommandResult {
    const s = this.state;
    const p = s.player;
    const t = s.market.tokens[tokenId];
    if (!t || t.phase === "dead") return fail("That token is dead. The bag is just a memory now.");
    const bal = balanceOf(t, "player");
    if (bal <= 0) return fail("You don't hold this token.");
    let amount = tokens ?? (pct !== undefined ? (bal * pct) / 100 : 0);
    if (!(amount > 0)) return fail("Enter an amount.");
    if (pct !== undefined && pct >= 100) amount = bal;
    amount = Math.min(amount, bal);
    const r = marketSell(s, t, "player", amount);
    if (!r.ok) return fail("Trade failed.");
    p.cash += r.usd;
    p.stats.trades++;
    onPlayerSell(s, tokenId);
    onPlayerSellOwnToken(s, this.rngEvents, t, r.tokens);
    const h = p.holdings.find((x) => x.tokenId === tokenId);
    const costPart = h ? h.costUsd * (r.tokens / bal) : 0;
    const pnl = r.usd - costPart;
    if (pnl > p.stats.bestTradeUsd) p.stats.bestTradeUsd = pnl;
    if (pnl < p.stats.worstTradeUsd) p.stats.worstTradeUsd = pnl;
    if (h) {
      h.costUsd -= costPart;
      if (balanceOf(t, "player") <= 0) p.holdings = p.holdings.filter((x) => x !== h);
    }
    return { ok: true, data: { usd: r.usd, tokens: r.tokens, fee: r.fee, pnl } };
  }

  /** Shared worlds: a server-confirmed fill. Token balances come from the server's ledger. */
  private applyFill(cmd: Extract<Command, { type: "APPLY_FILL" }>): CommandResult {
    const s = this.state;
    const p = s.player;
    if (!s.shared) return fail("Only in shared worlds.");
    p.stats.trades++;
    const h = p.holdings.find((x) => x.tokenId === cmd.tokenId);
    if (cmd.side === "buy") {
      p.cash = Math.max(0, p.cash - cmd.usd);
      const t = s.market.tokens[cmd.tokenId];
      if (t && s.tick - t.bornTick <= P.apeUnknown.maxAgeTicks) addHeat(p, P.apeUnknown.heat);
      if (h) h.costUsd += cmd.usd;
      else p.holdings.push({ kind: "token", tokenId: cmd.tokenId, costUsd: cmd.usd });
      return { ok: true, data: { usd: cmd.usd, tokens: cmd.tokens, fee: cmd.fee } };
    }
    p.cash += cmd.usd;
    onPlayerSell(s, cmd.tokenId);
    // Cost basis sold = share of the position this sell represents (server reports what's left).
    const costPart = h ? h.costUsd * (cmd.tokens / Math.max(1e-9, cmd.tokens + cmd.remaining)) : 0;
    const pnl = cmd.usd - costPart;
    if (pnl > p.stats.bestTradeUsd) p.stats.bestTradeUsd = pnl;
    if (pnl < p.stats.worstTradeUsd) p.stats.worstTradeUsd = pnl;
    if (h) {
      h.costUsd -= costPart;
      if (cmd.remaining <= 0) p.holdings = p.holdings.filter((x) => x !== h);
    }
    return { ok: true, data: { usd: cmd.usd, tokens: cmd.tokens, fee: cmd.fee, pnl } };
  }

  /** Convenience for UI lists. */
  liveTokens() {
    return liveTokens(this.state.market);
  }
}

function fail(error: string): CommandResult {
  return { ok: false, error };
}
